// server/browser/src/lib/downloads.ts
//
// Desktop-download catalog for the public Download page.
//
// Two builders, two file-name shapes:
//   Windows / macOS — script/build-installer.mjs (NSIS .exe / .dmg):
//       Optio-Pro_<version>_<arch>[-scope].<ext>
//   Linux — script/package-portable.sh (relocatable folder + tar.gz, NOT
//     AppImage/deb/rpm):
//       optio-<version>-linux-<arch>.tar.gz
// Only the Optio *Pro* build is published (Std is a test build).
// Caddy serves them from /releases/<version>/<file> — the same per-version
// layout as the CLI archives + latest.txt (see server/ionos/Caddyfile). This
// module is the single source of truth for those names so the page links and
// the publish scripts (script/publish-desktop-release.{sh,ps1}) never drift.

export type DesktopOs = "windows" | "macos" | "linux";

export interface DesktopVariant {
  /** Installer file name served under /releases/<version>/. */
  file: string;
  /** Short label for the (alternate) download link. */
  label: string;
  /** CPU arch the artifact targets. */
  arch: "x64" | "arm64";
}

export interface DesktopGroup {
  os: DesktopOs;
  /** Variants in preference order — the first is the card's primary CTA. */
  variants: DesktopVariant[];
}

/** Where Caddy serves the release tree (same origin as the SPA). */
export const RELEASES_BASE = "/releases";

/** Absolute-from-root URL for a published file at a given version. */
export function releaseUrl(version: string, file: string): string {
  return `${RELEASES_BASE}/${version}/${file}`;
}

/**
 * Every desktop installer we publish for `version`, grouped by OS and ordered
 * by preference (primary CTA first). The page HEAD-checks each file and only
 * renders the ones actually uploaded, so listing an arch here that a given
 * release didn't build simply hides it rather than producing a dead link.
 */
export function desktopCatalog(version: string): DesktopGroup[] {
  return [
    {
      os: "windows",
      variants: [
        { file: `Optio-Pro_${version}_x64-user.exe`, label: "Per-user · no admin", arch: "x64" },
        {
          file: `Optio-Pro_${version}_x64-machine.exe`,
          label: "Machine-wide · admin",
          arch: "x64",
        },
      ],
    },
    {
      os: "macos",
      variants: [
        { file: `Optio-Pro_${version}_arm64.dmg`, label: "Apple silicon", arch: "arm64" },
        { file: `Optio-Pro_${version}_x64.dmg`, label: "Intel", arch: "x64" },
      ],
    },
    {
      // Linux ships as a relocatable tar.gz (package-portable.sh), not AppImage.
      os: "linux",
      variants: [
        { file: `optio-${version}-linux-x64.tar.gz`, label: "tar.gz · x64", arch: "x64" },
        { file: `optio-${version}-linux-arm64.tar.gz`, label: "tar.gz · arm64", arch: "arm64" },
      ],
    },
  ];
}

/** Flat list of every installer file name for a version (tests + manifests). */
export function desktopFiles(version: string): string[] {
  return desktopCatalog(version).flatMap((g) => g.variants.map((v) => v.file));
}
